import { FocusEventHandler, useEffect } from 'react';
import styles from './BoxedAutoResizeTextarea.module.css';

interface TextareaProps<T extends string | number> {
  id: string;
  disabled: boolean;
  label?: JSX.Element;
  value: T;
  placeholder: string;
  setValue: (newValue: string) => void;
  errors?: string | string[] | null;
  // Formik element that triggers validation when blurred
  onBlur: FocusEventHandler<HTMLTextAreaElement>;
  // Formik element that determines if a user has visited a field
  touched: boolean | undefined;
  extraSmall?: boolean;
  scroll?: boolean;
}

// TODO: Add CSS to text area as used in the contact form

export const BoxedAutoResizeTextarea = <T extends string | number>({
  id,
  disabled,
  label,
  value,
  placeholder,
  setValue,
  errors,
  onBlur,
  touched,
  extraSmall,
  scroll,
}: TextareaProps<T>): JSX.Element => {
  function resizeTextarea(this: HTMLTextAreaElement) {
    this.style.height = 'auto';
    this.style.height = `${this.scrollHeight}` + 'px';
  }

  useEffect(() => {
    const textareaComponent = document.getElementById(`${id}`);
    if (textareaComponent) {
      textareaComponent.addEventListener('input', resizeTextarea);
      return () =>
        textareaComponent.removeEventListener('input', resizeTextarea);
    }
  }, []);

  const onChangeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    // setTextAreaHeight('auto');
    setValue(event.target.value);
  };

  return (
    <div>
      {label && (
        <label htmlFor={id} className={styles['label']}>
          {label}
        </label>
      )}
      <textarea
        id={id}
        aria-label={`${id}-textarea`}
        aria-invalid={errors ? true : false}
        aria-errormessage={`${id}-error-message`}
        disabled={disabled}
        value={value}
        placeholder={placeholder}
        onChange={onChangeHandler}
        onBlur={onBlur}
        rows={1}
        className={
          styles[scroll ? 'textarea3' : extraSmall ? 'textarea2' : 'textarea']
        }
        style={{
          borderColor: !touched
            ? 'var(--ea-black)'
            : errors
            ? 'var(--error-color)'
            : 'var(--ea-blue)',
        }}
      />
      {touched && errors && (
        <p id={`${id}-error-message`} className={styles['errors']}>
          {errors}
        </p>
      )}
    </div>
  );
};
